import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OutlinedInput, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { useState, useRef } from 'react';

import './styles.css';
import ModalFeedBack from '../../../components/ModalFeedBack';
import LoadingApp from '../../../components/LoadingApp';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { onlyNumbers } from '../../../utils/OnlyNumbers';
import { sxLabel, sxTypographyTitle, sxValidationMessage } from './sxProps';
import DefaultCheckbox from '../../../components/Accessibility/DefaultCheckbox';
import DefaultText from '../../../components/Accessibility/DefaultText';
import FilterSelect from '../../../components/FilterSelect';
import { FilterInterface } from '../../../interfaces/entities/FilterInterface';
import DateInput from '../../../components/DateInput';
import dayjs from 'dayjs';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import { getComprovantePagamento } from '../../../services/ComprovanteService';
import { InformePagamentosValidation } from '../../../interfaces/validation/InformePagamentosValidation';
import { base64toBlob } from '../../../utils/Base64ToBlob';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const InformeForm: React.FC<{}> = () => {

	const [showModalFeedBack, setShowModalFeedBack] = useState<boolean>(false);
	const [subTitleModal, setSubTituloModal] = useState('');
	const [showLoading, setShowLoading] = useState<boolean>(false);

	const [anoSelecionado, setAnoSelecionado] = useState<string>('');
	const [captchaToken, setCaptchaToken] = useState<string>('');
	const [captchaError, setCaptchaError] = useState<string>('');
	const captchaRef = useRef<HCaptcha>(null);

	const { textStyleAccessibility } = useAccessibility();

	const formik = useFormik({
		initialValues: { numeroCartao: '', cpf: '', dataNascimento: '', noCardChecked: false },
		validationSchema: InformePagamentosValidation,
		onSubmit: (values) => {
			if (!captchaToken) {
				setCaptchaError('Por favor, complete a verificação do captcha');
				return;
			}

			setShowLoading(true);
			getComprovantePagamento(
				parseInt(anoSelecionado), 
				values.noCardChecked ? '' : values.numeroCartao, 
				values.cpf, 
				values.noCardChecked ? values.dataNascimento : '',
				captchaToken
			).then(pdf => {
				const blob = base64toBlob(pdf || '');
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl);
				captchaRef.current?.resetCaptcha();
				setCaptchaToken('');
			}).catch(error => {
				console.log(error.message);
				setShowModalFeedBack(true);
				setSubTituloModal(error?.message || 'Erro ao buscar Informe de Pagamentos');
				captchaRef.current?.resetCaptcha();
				setCaptchaToken('');
			}).finally(() => {
				setShowLoading(false);
			});
		},
		validateOnChange: false,
	});

	const anos: FilterInterface[] = [
		{ id: '2020', descricao: '2020'},
		{ id: '2021', descricao: '2021'},
		{ id: '2022', descricao: '2022'},
		{ id: '2023', descricao: '2023'},
		{ id: '2024', descricao: '2024'},
		{ id: '2025', descricao: '2025'},
	]

	const handleModalFeedBack = (): void => {
		setShowModalFeedBack(show => !show);
	}

	const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
		formik.setFieldValue('noCardChecked', event.target.checked);
	};

	const handleDateChange = (newDate: Date | null) => {
		const formattedDate = newDate ? dayjs(newDate).format("DD/MM/YYYY") : "";
		formik.setFieldValue('dataNascimento', formattedDate);
	};

	const onVerifyCaptcha = (token: string) => {
		setCaptchaToken(token);
		setCaptchaError('');
	};

	const onExpireCaptcha = () => {
		setCaptchaToken('');
		setCaptchaError('O captcha expirou. Por favor, complete novamente.');
	};

	const onErrorCaptcha = (err: string) => {
		console.error('Erro no captcha:', err);
		setCaptchaError('Erro ao carregar o captcha. Por favor, tente novamente.');
	};

	const enableButton = formik.values.cpf 
										&& anoSelecionado
										&& captchaToken
										&& (formik.values.noCardChecked ? formik.values.dataNascimento : formik.values.numeroCartao);
									
	return (
		<div className='width-100'>
			<ModalFeedBack
				showModal={showModalFeedBack}
				handleShowModal={handleModalFeedBack}
				icon={'error'}
				textButton={'Fechar'}
				title={'Erro'}
				subTitle={subTitleModal}
				showButtonModal
				handleButtonModal={handleModalFeedBack}
				classNameModal='modal-feedback-error'
			/>
			<LoadingApp showLoading={showLoading} />
			<form className='width-100 login-container-end' onSubmit={formik.handleSubmit}>
				<div style={textStyleAccessibility} className='container-login-painel-rigth'>
					<Typography 
						style={textStyleAccessibility} 
						sx={sxTypographyTitle} 
						className='margin-bottom-painel-rigth'>
							Informe de Pagamentos
					</Typography>
					
					<div className='width-100 display-flex flex-col gap-20'>
						{ !formik.values.noCardChecked ?
							<div className='width-100'>
								<label aria-label="Número do cartão do titular" htmlFor="numeroCartaoField" />
								<Typography style={textStyleAccessibility} sx={sxLabel}>Número do Cartão do Titular</Typography>
								<OutlinedInput
									aria-describedby='errNumeroCartao'
									id="numeroCartaoField"
									fullWidth
									type={'number'}
									value={formik.values.numeroCartao}
									placeholder='Exemplo: 99999999999999999'
									onChange={(event) => { formik.setFieldValue('numeroCartao', onlyNumbers(event.target.value)) }}
									name='numeroCartao'
									error={!!formik.errors.numeroCartao}
									maxRows={1}
									inputProps={{ maxLength: 30 }}
								/>
								{(formik.errors.numeroCartao) &&
									<div className='container-validation-message'>
										<FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
										<Typography id='errNumeroCartao' sx={sxValidationMessage} style={textStyleAccessibility} >{formik.errors.numeroCartao || 'Verifique se o CPF ou cartão está correto'}</Typography>
									</div>
								}
							</div> :
							<div className='width-100'>
								<Typography style={textStyleAccessibility} sx={sxLabel}>Data de Nascimento do Titular</Typography>
								<DateInput
									label={`Data de nascimento do titular ${formik.errors.dataNascimento ?? ''}`}
									slotProps={{
										textField: {
											placeholder: "01/01/2000",
											error: !!formik.errors.dataNascimento,
										},
									}}
									onChange={(newDate) => handleDateChange(newDate)}
									value={formik.values.dataNascimento ? dayjs(formik.values.dataNascimento, "DD/MM/YYYY") : null}
									maxDate={dayjs().subtract(1, "day")}
								/>
								{formik.errors.dataNascimento &&
									<div className="container-validation-message">
										<FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
										<Typography sx={sxValidationMessage} style={textStyleAccessibility} >{formik?.errors?.dataNascimento}</Typography>
									</div>
								}
							</div>
						}

						<div className='flex-row' style={{ marginLeft: '-10px' }}>
							<DefaultCheckbox onChange={handleCheck}/>
							<DefaultText sx={{fontSize: '14px'}} text="Não tenho ou não sei o número do cartão do titular" />
				  	</div>
						<div className='width-100'>
							<Typography style={textStyleAccessibility} sx={sxLabel}>CPF do Titular</Typography>
							<label aria-label="CPF do tituloar" htmlFor="cpfField" />
							<OutlinedInput
								aria-describedby='errCpf'
								fullWidth
								type={'number'}
								value={formik.values.cpf}
								placeholder='Exemplo: 99999999999'
								onChange={(event) => { formik.setFieldValue('cpf', onlyNumbers(event.target.value)) }}
								name='cpf'
								id="cpfField"
								error={!!formik.errors.cpf}
								maxRows={1}
								inputProps={{ maxLength: 30 }}
							/>
							{(formik.errors.cpf) &&
								<div className='container-validation-message'>
									<FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
									<Typography id='errCpf' sx={sxValidationMessage} style={textStyleAccessibility} >{formik.errors.cpf || 'Verifique se o CPF ou cartão está correto'}</Typography>
								</div>
							}
						</div>
							<div>
							<FilterSelect title='Ano do Informe de Pagamento' options={anos} disabled={false} handleChange={setAnoSelecionado} />
						</div>
						
						<div className='width-100' style={{ marginTop: '20px' }}>
							<HCaptcha 
								ref={captchaRef}
								sitekey={process.env.REACT_APP_HCAPTCHA_KEY || ''}
								onVerify={onVerifyCaptcha}
								onExpire={onExpireCaptcha}
								onError={onErrorCaptcha}
							/>
							{captchaError && (
								<div className='container-validation-message'>
									<FontAwesomeIcon icon={faCircleExclamation} size='sm' color='#D84141' />
									<Typography sx={sxValidationMessage} style={textStyleAccessibility}>
										{captchaError}
									</Typography>
								</div>
							)}
						</div>

						<DefaultButton text="Gerar informe" type="submit" disabled={!enableButton} style={{ marginTop: '10px' }}/>
					</div>
				</div>			
			</form>
		</div>
	);
};

export default InformeForm;
